import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'sonner'
import '@/app/globals.css'
import { PwaRegister } from '@/components/pwa-register'
import { trackKeyboardInset } from '@/lib/keyboard-inset'
import { keepFocusedFieldInSight } from '@/lib/field-in-sight'
import { installDecimalComma } from '@/lib/decimal-comma'
import { rememberInstallChannel } from '@/lib/channel'
import { listenForUncaughtErrors } from '@/lib/error-report'
import { getLang, loadLang } from '@/lib/i18n'
import { MENU_HOST } from '@/lib/brand'
import { App } from './App'

// After a deploy the previous build's chunks are gone; a window still running
// the old build fails to import the next page it opens. Vite reports that as
// `vite:preloadError` — reload ONCE to pick up the new build (a second failure
// within a minute is a real outage: let the error screen show it).
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem('chunk-reload-at') || 0)
    if (Date.now() - last < 60_000) return
    sessionStorage.setItem('chunk-reload-at', String(Date.now()))
  } catch { /* storage blocked: reload anyway, once per page load */ }
  event.preventDefault()
  window.location.reload()
})

// Phones: dialogs and sheets dock above the on-screen keyboard, not under it;
// and the field being typed into follows its dialog as it shrinks.
trackKeyboardInset()
keepFocusedFieldInSight()

// 1.72 — „12,5" typed into any number field stays 12.5: Chrome dropped the comma and made it 125.
installDecimalComma()

// The Google Play build opens `/pos?src=play` — note it before the router
// reads the address (lib/channel.ts says what that hides and why).
rememberInstallChannel()

// 111 — an exception or a rejected promise nothing caught goes to the morning
// digest instead of vanishing in a console nobody opens.
listenForUncaughtErrors()

/**
 * SPA entry point — the replacement for `app/layout.tsx`.
 *
 * What moved where: the <html>/<body> attributes, the fonts, the meta tags and
 * the pre-paint theme script are all in `index.html` now, because they must
 * exist before any JavaScript runs. What is left here is what React owns.
 */
// A device that chose another language fetches its dictionary before the
// first paint, so the till never flashes Georgian first. Georgian: at once.
const render = () => createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <PwaRegister />
      <App />
      <Toaster
        position="top-right"
        // Phones: below the header, not on it. A toast over the top bar hid
        // the back / close buttons exactly when someone reached for them
        // (owner's stocktake screenshot, 2026-09-11).
        mobileOffset={{ top: 'calc(env(safe-area-inset-top) + 4rem)' }}
        richColors
        toastOptions={{
          style: {
            fontFamily: 'var(--font-plus-jakarta), system-ui, sans-serif',
            borderRadius: '0.875rem',
          },
        }}
      />
    </BrowserRouter>
  </StrictMode>,
)
// 1.69 — never on the guests' menu host: index.html's first script already sent that page to /t and stopped the
// rest of the file; this is the second line, so no till and no service worker (PwaRegister) ever start there.
if (location.hostname !== MENU_HOST) loadLang(getLang()).catch(() => {}).finally(render)
